/**
 * Men.jsx — v2 Refactored
 *
 * 🚨 Critical Bugs Fixed:
 *  1. fetchSubcategories creates a nested AbortController inside an outer
 *     signal — if the outer is aborted, the inner subController is not.
 *     Sub-fetch continues after unmount → setState on dead component.
 *     Fixed: single controller per useEffect, passed through all fetches.
 *  2. fetchProducts called directly in retry handlers with a new
 *     AbortController that is never stored or cleaned up — memory leak
 *     and potential setState after unmount on retry path.
 *     Fixed: fetchKey counter pattern — useEffect handles cleanup.
 *  3. Skeleton `key={i}` index — when count changes (loading→loaded),
 *     React reuses keys on real cards causing flicker. Fixed: `skel-${i}`.
 *  4. ProductCard `key={product._id || product.id}` — if both exist and
 *     alternate between renders, cards unmount/remount. Fixed: normalise
 *     to `pid` in a transform before setProducts.
 *  5. `aria-live="polite"` on product count fires on every filter change
 *     before the fetch even starts — announces stale count. Fixed: only
 *     rendered after loading=false with a confirmed product count.
 *  6. Retry handlers call setState directly without checking if mounted —
 *     no AbortController cleanup on the retry-spawned fetch.
 *     Fixed: all fetches are useEffect-driven with stable cleanup.
 *
 * ⚡ Performance Fixed:
 *  7. fetchSubcategories in useCallback with [location.pathname, BASE_URL]
 *     — BASE_URL is a module constant, not reactive; remove from dep array.
 *  8. fetchProducts in useCallback re-creates on [showToast] dep — if
 *     ToastContext recreates showToast each render, infinite fetch loop.
 *     Fixed: showToastRef stable ref pattern.
 *  9. collectionSchema and breadcrumbSchema computed inline in JSX on
 *     every render. Fixed: useMemo with stable deps.
 * 10. Skeleton uses CSS opacity pulse (m-pulse) — causes layout paint.
 *     Fixed: compositor-only transform-shimmer via ::after pseudo.
 * 11. Hero <img> has no width/height — CLS on load. Fixed: explicit
 *     width/height + aspect-ratio CSS on the wrapper.
 * 12. Recently-viewed fetch has no de-duplication guard — fires even
 *     when IDs haven't changed. Fixed: stable dep on serialised ID string.
 *
 * 🔍 SEO Fixed:
 * 13. Twitter Card meta tags entirely missing. Added.
 * 14. og:image is a hardcoded "/og-image.jpg" with no per-subcategory
 *     variant — sharing /men/round-neck shows same image as homepage.
 *     Fixed: per-route og:image mapping.
 * 15. collectionSchema emits products before loading is complete —
 *     schema flickers from empty→populated on hydration. Fixed: only
 *     emit ItemList when products.length > 0 AND !loading.
 * 16. Breadcrumb separator uses an Icon component (<IconChevronRight>)
 *     — if the Icon SVG has no aria-hidden, it's announced as "image".
 *     Fixed: aria-hidden="true" enforced; CSS ::after fallback too.
 *
 * 📱 Mobile Fixed:
 * 17. Filter bar chips have no min-width and overflow-x auto — on 320px
 *     the filter button + sort select eat all space, chips are 0-wide.
 *     Fixed: filter btn and sort select have max-width caps; chips flex.
 * 18. Hero CTA buttons have no min-height on mobile — can be < 44px.
 *     Fixed: min-height: 46px enforced in CSS.
 * 19. Pagination buttons have no min touch target on mobile. Fixed: 44px.
 *
 * 🎯 Conversion Fixed:
 * 20. Social bar hardcodes "124 reviews" and "2,000+ customers" but these
 *     are never updated from API — will go stale. Moved to constants.
 * 21. Empty state when filters active only shows "Clear Filters" — no
 *     link to the parent category. Added "Browse All Men's" always.
 * 22. Recently viewed has no "View all" — truncated to 5, no escape.
 *     Added link to a dedicated recently-viewed page.
 * 23. Product grid on subcategory page has no count shown to user —
 *     "120 products" is a strong social-proof / inventory signal.
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import FilterDrawer from '../../components/filters/FilterDrawer/FilterDrawer';
import ProductCard from '../../components/product/ProductCard/ProductCard';
import { useToast } from '../../context/ToastContext';
import { getRecentlyViewed, saveRecentlyViewed } from '../../utils/recentlyViewed';
import {
  IconShield, IconTruck, IconReturn, IconLock,
  IconStar, IconFilter, IconArrowRight, IconChevronRight,
} from '../../components/common/Icons/Icons';
import { formatCurrency } from '../../utils/currency';
import { getImageUrl } from '../../utils/imageUtils';
import ApiImage, { ensureAbsoluteSrc } from '../../components/common/ApiImage';
import '../../pages/shared/recentlyViewed.css';
import '../../pages/shared/pagination.css';
import './Men.css';

// ─────────────────────────────────────────────────────────────────────────────
// Constants — single source of truth for the whole site
// ─────────────────────────────────────────────────────────────────────────────

const BRAND_NAME    = 'EVORA';
const BRAND_URL     = 'https://evoraclothing.com';
// Free shipping on all orders (no threshold). Returns: size & defective only, 7 days (see /returns).
const RETURNS_DAYS  = '7';

// ✅ FIX #20 — social proof numbers in one place, easy to update
const SOCIAL_RATING     = 4.8;
const SOCIAL_REVIEWS    = 124;
const SOCIAL_CUSTOMERS  = '2,000+';

const BASE_URL = (() => {
  const url = process.env.REACT_APP_API_URL;
  if (!url && process.env.NODE_ENV === 'production') {
    throw new Error('REACT_APP_API_URL is not set.');
  }
  return url || 'http://localhost:5000/api';
})();

const SORT_OPTIONS = [
  { value: 'popular',    label: 'Popular'            },
  { value: 'newest',     label: 'Newest'             },
  { value: 'price-low',  label: 'Price: Low → High'  },
  { value: 'price-high', label: 'Price: High → Low'  },
];

// ✅ FIX #14 — per-route OG images
const OG_IMAGES = {
  'round-neck': `${BRAND_URL}/og/men-roundneck.jpg`,
  polo:         `${BRAND_URL}/og/men-polo.jpg`,
  default:      `${BRAND_URL}/og/men-collection.jpg`,
};

// ─────────────────────────────────────────────────────────────────────────────
// SEO meta — defined once, not inline in JSX
// ─────────────────────────────────────────────────────────────────────────────

const SUBCATEGORY_META = {
  'round-neck': {
    title:       `Men's Round Neck T-Shirts | Premium Cotton | ${BRAND_NAME}`,
    description: `Shop premium round neck t-shirts for men at ${BRAND_NAME}. High-quality cotton, refined fit. Free shipping on all orders.`,
    h1:          "Men's Round Neck T-Shirts",
    subtitle:    'Premium round neck tees crafted for comfort and refined everyday wear.',
    ogImage:     OG_IMAGES['round-neck'],
  },
  polo: {
    title:       `Men's Polo T-Shirts | Premium Polo Shirts | ${BRAND_NAME}`,
    description: `Discover premium polo t-shirts for men at ${BRAND_NAME}. Classic collared design, modern fit. Free shipping on all orders.`,
    h1:          "Men's Polo T-Shirts",
    subtitle:    'Classic collared polo shirts with a modern fit and premium finish.',
    ogImage:     OG_IMAGES.polo,
  },
};

const DEFAULT_META = {
  title:       `Men's Premium T-Shirts | Round Neck & Polo | ${BRAND_NAME}`,
  description: `Discover premium round neck and polo t-shirts for men at ${BRAND_NAME}. Free shipping on all orders. Returns for size & defective only (${RETURNS_DAYS} days).`,
  h1:          'Shop Men',
  subtitle:    'Timeless pieces designed for refined everyday wear.',
  ogImage:     OG_IMAGES.default,
};

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

// Stable id for keys (no Math.random — hydration-safe and stable across re-renders)
function normaliseProducts(raw = []) {
  return raw.map((p, i) => ({ ...p, pid: p._id ?? p.id ?? `list-${i}` }));
}

const trackEvent = (name, data = {}) => {
  if (typeof window !== 'undefined' && window.gtag) window.gtag('event', name, data);
  if (process.env.NODE_ENV === 'development') console.log('[Analytics]', name, data);
};

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current - 2, current + 1, current + 2]);
  const valid = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < valid.length; i++) {
    if (i > 0 && valid[i] - valid[i - 1] > 1) result.push('...');
    result.push(valid[i]);
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Men — Page component
// ─────────────────────────────────────────────────────────────────────────────

function Men() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Path parsing (supports /men and /shop/men) ─────────────────────────────
  const { basePath, categorySlug, pathSubCategory } = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const isShopMen = parts[0] === 'shop' && parts[1] === 'men';
    if (isShopMen) {
      return {
        basePath: '/shop/men',
        categorySlug: 'men',
        pathSubCategory: parts.length >= 3 ? parts[2] : null,
      };
    }
    return {
      basePath: '/men',
      categorySlug: parts[0] || 'men',
      pathSubCategory: parts.length >= 2 ? parts[1] : null,
    };
  }, [location.pathname]);

  const isMainPage = !pathSubCategory;

  // ── URL params ───────────────────────────────────────────────────────────
  const sort          = searchParams.get('sort')           || 'popular';
  const page          = parseInt(searchParams.get('page')) || 1;
  const sizeParam     = searchParams.get('size')           || '';
  const priceMinParam = searchParams.get('priceMin')       || '';
  const priceMaxParam = searchParams.get('priceMax')       || '';
  const isNewParam    = searchParams.get('isNew') === 'true';

  const filters = useMemo(() => ({
    size:         sizeParam ? [sizeParam] : [],
    priceMin:     priceMinParam,
    priceMax:     priceMaxParam,
    subCategory:  pathSubCategory ? [pathSubCategory] : [],
    isNew:        isNewParam,
  }), [sizeParam, priceMinParam, priceMaxParam, pathSubCategory, isNewParam]);

  const activeFilterCount =
    (filters.size?.length || 0) +
    ((priceMinParam || priceMaxParam) ? 1 : 0) +
    (isNewParam ? 1 : 0);

  // ── State ────────────────────────────────────────────────────────────────
  const [category,        setCategory]        = useState(null);
  const [subcategories,   setSubcategories]   = useState([]);
  const [products,        setProducts]        = useState([]);
  const [recentlyViewed,  setRecentlyViewed]  = useState([]);

  const [catLoading,  setCatLoading]  = useState(true);
  const [prodLoading, setProdLoading] = useState(!isMainPage);
  const [catError,    setCatError]    = useState(null);
  const [subError,    setSubError]    = useState(null);
  const [prodError,   setProdError]   = useState(null);

  const [totalPages,    setTotalPages]    = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasNextPage,   setHasNextPage]   = useState(false);
  const [hasPrevPage,   setHasPrevPage]   = useState(false);
  const [isFilterOpen,  setIsFilterOpen]  = useState(false);

  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  // Hero image URL (defined before useEffect that uses heroSrc)
  const heroFallbackSrc =
    pathSubCategory === 'round-neck'
      ? '/men-roundneck-hero.jpg'
      : pathSubCategory === 'polo'
        ? '/men-polo-hero.jpg'
        : '/men-hero.jpg';
  // Subcategory pages: use uploaded subcategory image when available, else static. Main /men: wait for category fetch.
  const matchingSub = pathSubCategory && subcategories.find(s => s.slug === pathSubCategory);
  const heroSrc =
    pathSubCategory && (pathSubCategory === 'round-neck' || pathSubCategory === 'polo')
      ? (matchingSub?.image ? getImageUrl(matchingSub.image, matchingSub.updatedAt, heroFallbackSrc) : heroFallbackSrc)
      : catLoading
        ? null
        : (category?.image
            ? getImageUrl(category.image, category.updatedAt, '/hero.png')
            : heroFallbackSrc);

  // ✅ FIX #2 — fetchKey triggers clean useEffect re-runs on retry
  const [catFetchKey,  setCatFetchKey]  = useState(0);
  const [prodFetchKey, setProdFetchKey] = useState(0);

  // ✅ FIX #8 — stable ref for showToast
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  // Reset hero image loaded state when heroSrc changes so we don't show stale image
  useEffect(() => { setHeroImageLoaded(false); }, [heroSrc]);

  // ── Current SEO meta ─────────────────────────────────────────────────────
  const currentMeta = (pathSubCategory && SUBCATEGORY_META[pathSubCategory])
    ? SUBCATEGORY_META[pathSubCategory]
    : DEFAULT_META;

  /**
   * Canonicalization strategy (SPA + faceted navigation):
   * - Consolidate duplicate entry routes: prefer `/men/...` over `/shop/men/...`
   * - Faceted/sort URLs: noindex,follow + canonical to the clean base URL
   * - Pagination: canonical includes `?page=N` only on clean (non-faceted) URLs
   */
  const preferredBasePath = categorySlug === 'men' ? '/men' : basePath;

  const canonicalUrl = `${BRAND_URL}${preferredBasePath}${pathSubCategory ? `/${pathSubCategory}` : ''}`;

  const hasFacetParams =
    !!sizeParam ||
    !!priceMinParam ||
    !!priceMaxParam ||
    !!isNewParam ||
    (sort && sort !== 'popular');

  const canonicalHref = hasFacetParams
    ? canonicalUrl
    : `${canonicalUrl}${page > 1 ? `?page=${page}` : ''}`;

  const robotsContent = hasFacetParams
    ? 'noindex, follow'
    : 'index, follow, max-snippet:-1, max-image-preview:large';

  const pageUrlForOg = canonicalHref;

  const listBasePath = pathSubCategory
    ? `${preferredBasePath}/${pathSubCategory}`
    : preferredBasePath;

  const buildListUrl = (targetPage) => {
    const p = new URLSearchParams(searchParams);
    if (targetPage && targetPage > 1) p.set('page', String(targetPage));
    else p.delete('page');
    const qs = p.toString();
    return `${listBasePath}${qs ? `?${qs}` : ''}`;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Fetch subcategories — ✅ FIX #1: single controller, no nested controllers
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function run(attempt = 0) {
      setCatLoading(true);
      setCatError(null);
      setSubError(null);

      const timeout = setTimeout(() => controller.abort('timeout'), 30_000);

      try {
        // Step 1 — fetch category by slug
        const catRes = await fetch(`${BASE_URL}/categories/slug/${categorySlug}`, { signal, cache: 'no-store' });
        clearTimeout(timeout);
        if (!catRes.ok) throw Object.assign(new Error(`HTTP ${catRes.status}`), { status: catRes.status });

        const catData = await catRes.json();
        if (signal.aborted) return;
        if (!catData.success || !catData.category) { setCatError('Category not found.'); return; }

        // Store category data (includes image)
        setCategory(catData.category);

        // Step 2 — fetch subcategories using category ID
        const subTimeout = setTimeout(() => controller.abort('timeout'), 30_000);
        const subRes = await fetch(
          `${BASE_URL}/subcategories?categoryId=${catData.category._id}`,
          { signal, cache: 'no-store' },
        );
        clearTimeout(subTimeout);
        if (!subRes.ok) throw Object.assign(new Error(`HTTP ${subRes.status}`), { isSub: true });

        const subData = await subRes.json();
        if (signal.aborted) return;
        if (subData.success) setSubcategories(subData.subcategories ?? []);

      } catch (err) {
        clearTimeout(timeout);
        if (signal.aborted) return; // unmounted or timed out — silent

        const MAX = 2;
        if (attempt < MAX) {
          const delay = Math.min(1_000 * 2 ** attempt, 5_000);
          setTimeout(() => { if (!signal.aborted) run(attempt + 1); }, delay);
          return;
        }
        // All retries exhausted
        if (err.isSub) {
          setSubError('Failed to load subcategories. Please refresh.');
        } else {
          setCatError('Failed to load category. Please refresh.');
        }
      } finally {
        if (!signal.aborted) setCatLoading(false);
      }
    }

    run();
    return () => controller.abort();
  // catFetchKey triggers retry; location.pathname triggers route change re-fetch
  }, [location.pathname, catFetchKey, categorySlug]);

  // ─────────────────────────────────────────────────────────────────────────
  // Fetch products — ✅ FIX #1 #2 #6: single clean controller
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isMainPage) return;

    const controller = new AbortController();
    const { signal } = controller;

    async function run(attempt = 0) {
      setProdLoading(true);
      setProdError(null);

      const timeout = setTimeout(() => controller.abort('timeout'), 30_000);

      try {
        const params = new URLSearchParams({
          limit:  '20',
          sort,
          page:   String(page),
          status: 'active',
          categorySlug,
        });
        if (pathSubCategory) params.append('subcategorySlug', pathSubCategory);
        if (sizeParam)       params.append('size',         sizeParam);
        if (priceMinParam)   params.append('priceMin',     priceMinParam);
        if (priceMaxParam)   params.append('priceMax',     priceMaxParam);
        if (isNewParam)      params.append('isNew',       'true');

        const res = await fetch(`${BASE_URL}/products?${params}`, { signal, cache: 'no-store' });
        clearTimeout(timeout);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        if (signal.aborted) return;

        if (data.success) {
          // ✅ FIX #4 — normalise IDs before setting state
          setProducts(normaliseProducts(data.products ?? []));
          setTotalPages(data.totalPages ?? 1);
          setTotalProducts(data.totalProducts ?? data.total ?? 0);
          setHasNextPage(data.pagination?.hasNextPage ?? page < (data.totalPages ?? 1));
          setHasPrevPage(data.pagination?.hasPrevPage ?? page > 1);
        }
      } catch (err) {
        clearTimeout(timeout);
        if (signal.aborted) return;

        const MAX = 3;
        if (attempt < MAX) {
          const delay = Math.min(1_000 * 2 ** attempt, 10_000);
          setTimeout(() => { if (!signal.aborted) run(attempt + 1); }, delay);
          return;
        }
        setProdError('Failed to load products. Please try again.');
        showToastRef.current("We couldn't load these products. Check your connection and try again.", 'error');
      } finally {
        if (!signal.aborted) setProdLoading(false);
      }
    }

    run();
    return () => controller.abort();
  }, [
    isMainPage, pathSubCategory, categorySlug,
    sort, page, sizeParam, priceMinParam, priceMaxParam, isNewParam,
    prodFetchKey,
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // Recently viewed — robust to legacy object entries in storage; dedupe IDs
  // ─────────────────────────────────────────────────────────────────────────
  const rvEntries = getRecentlyViewed().slice(0, 5);
  const rvIds = rvEntries
    .map((entry) => {
      if (typeof entry === 'object' && entry !== null) {
        return (entry._id || entry.id || '').toString().trim();
      }
      return typeof entry === 'string' ? entry.trim() : String(entry).trim();
    })
    .filter(Boolean);
  const rvIdsUnique = rvIds.filter((id, i) => rvIds.indexOf(id) === i);
  const rvIdString = rvIdsUnique.join(',');

  useEffect(() => {
    if (!rvIdString) return;

    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort('timeout'), 30_000);
    const idsOrder   = rvIdString.split(',').filter(Boolean);

    fetch(`${BASE_URL}/products?ids=${rvIdString}`, { signal: controller.signal, cache: 'no-store' })
      .then((r) => { clearTimeout(timeout); if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => {
        if (controller.signal.aborted || !d.success) return;
        const products = d.products ?? [];
        const ordered = idsOrder
          .map((id) => products.find((p) => (p._id || p.id)?.toString() === id))
          .filter(Boolean);
        setRecentlyViewed(ordered);
      })
      .catch(() => { clearTimeout(timeout); /* silent — recently viewed is non-critical */ });

    return () => { clearTimeout(timeout); controller.abort(); };
  }, [rvIdString]);

  // ── Analytics ─────────────────────────────────────────────────────────────
  useEffect(() => {
    trackEvent('page_view', {
      page_title:    currentMeta.title,
      page_location: window.location.href,
    });
  }, [currentMeta.title]);

  // ── Memoised schemas — ✅ FIX #9 ────────────────────────────────────────
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type':    'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BRAND_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Men',  item: `${BRAND_URL}${preferredBasePath}` },
      ...(pathSubCategory ? [{
        '@type': 'ListItem', position: 3,
        name:    currentMeta.h1,
        item:    canonicalUrl,
      }] : []),
    ],
  }), [pathSubCategory, currentMeta.h1, canonicalUrl, preferredBasePath]);

  // ✅ FIX #15 — only include ItemList when products are actually loaded
  const collectionSchema = useMemo(() => ({
    '@context':  'https://schema.org',
    '@type':     'CollectionPage',
    name:        currentMeta.title,
    description: currentMeta.description,
    url:         canonicalUrl,
    ...(!prodLoading && products.length > 0 && {
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: products.slice(0, 10).map((p, i) => ({
          '@type':   'ListItem',
          position:  i + 1,
          url:       `${BRAND_URL}/product/${p.slug ?? p.pid}`,
          name:      p.name,
        })),
      },
    }),
  }), [currentMeta, canonicalUrl, products, prodLoading]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFiltersChange = useCallback((newFilters) => {
    const selectedSub = newFilters.subCategory?.[0] ?? newFilters.subcategory?.[0] ?? null;
    const params      = new URLSearchParams();
    if (newFilters.size?.[0])   params.set('size',     newFilters.size[0]);
    if (newFilters.priceMin)   params.set('priceMin', newFilters.priceMin);
    if (newFilters.priceMax)  params.set('priceMax', newFilters.priceMax);
    if (newFilters.isNew)      params.set('isNew',   'true');
    if (sort !== 'popular')    params.set('sort',     sort);
    const subExists = subcategories.some((s) => s.slug === selectedSub);
    const path = selectedSub && subExists ? `${preferredBasePath}/${selectedSub}` : preferredBasePath;
    navigate(`${path}${params.toString() ? `?${params}` : ''}`, { replace: false });
  }, [navigate, sort, subcategories, preferredBasePath]);

  const handleSortChange = useCallback((e) => {
    const p = new URLSearchParams(searchParams);
    p.set('sort', e.target.value);
    p.delete('page');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  const handlePageChange = useCallback((newPage) => {
    const p = new URLSearchParams(searchParams);
    if (newPage && newPage > 1) p.set('page', String(newPage));
    else p.delete('page');
    setSearchParams(p, { replace: true });
    requestAnimationFrame(() => {
      document.querySelector('.men-products')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [searchParams, setSearchParams]);

  const handleAddToCart = useCallback((productName, productId) => {
    showToastRef.current(`${productName} added to your bag — check it out when you're ready.`, 'success');
    trackEvent('add_to_cart', {
      currency: 'INR',
      value:    products.find((p) => p.pid === productId)?.price ?? 0,
      items:    [{ item_id: productId, item_name: productName }],
    });
  }, [products]);

  const handleProductClick = useCallback((product) => {
    saveRecentlyViewed(product.pid);
    trackEvent('select_item', {
      item_list_id:   'shop_men',
      item_list_name: 'Shop Men',
      items: [{ item_id: product.pid, item_name: product.name, price: product.price }],
    });
  }, []);

  const clearFilters = useCallback(() => {
    handleFiltersChange({
      size: [], priceMin: '', priceMax: '',
      subCategory: pathSubCategory ? [pathSubCategory] : [],
      isNew: false,
    });
  }, [handleFiltersChange, pathSubCategory]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <Helmet>
        <title>{currentMeta.title}{page > 1 ? ` — Page ${page}` : ''}</title>
        <meta name="description" content={currentMeta.description} />
        <meta name="robots" content={robotsContent} />
        <link rel="canonical" href={canonicalHref} />
        {!hasFacetParams && hasPrevPage && <link rel="prev" href={`${canonicalUrl}${page - 1 > 1 ? `?page=${page - 1}` : ''}`} />}
        {!hasFacetParams && hasNextPage && <link rel="next" href={`${canonicalUrl}?page=${page + 1}`} />}

        {/* Preload the hero image only when we know the final URL (avoids loading fallback then switching) */}
        {heroSrc && (
          <link rel="preload" as="image" href={ensureAbsoluteSrc(heroSrc)} fetchPriority="high" />
        )}

        {/* Open Graph */}
        <meta property="og:type"         content="website" />
        <meta property="og:url"          content={pageUrlForOg} />
        <meta property="og:site_name"    content={BRAND_NAME} />
        <meta property="og:title"        content={currentMeta.title} />
        <meta property="og:description"  content={currentMeta.description} />
        <meta property="og:image"        content={currentMeta.ogImage} />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt"    content={currentMeta.h1} />
        <meta property="og:locale"       content="en_IN" />

        {/* ✅ FIX #13 — Twitter Card was entirely missing */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={currentMeta.title} />
        <meta name="twitter:description" content={currentMeta.description} />
        <meta name="twitter:image"       content={currentMeta.ogImage} />
        <meta name="twitter:image:alt"   content={currentMeta.h1} />

        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      </Helmet>

      <div className="men-page">

        {/* ── Breadcrumb ─────────────────────────────────────────────────── */}
        <nav className="men-breadcrumb" aria-label="Breadcrumb">
          <ol className="men-breadcrumb__list">
            <li>
              <Link to="/" className="men-breadcrumb__link">Home</Link>
            </li>
            {/* ✅ FIX #16 — aria-hidden on separator */}
            <li className="men-breadcrumb__sep" aria-hidden="true" />
            {pathSubCategory ? (
              <>
                <li>
                  <Link to={preferredBasePath} className="men-breadcrumb__link">Men</Link>
                </li>
                <li className="men-breadcrumb__sep" aria-hidden="true" />
                <li>
                  <span className="men-breadcrumb__current" aria-current="page">
                    {pathSubCategory === 'round-neck' ? 'Round Neck' : 'Polo'}
                  </span>
                </li>
              </>
            ) : (
              <li>
                <span className="men-breadcrumb__current" aria-current="page">Men</span>
              </li>
            )}
          </ol>
        </nav>

        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section className="men-hero" aria-labelledby="men-hero-title">
          {/* Skeleton until we have a hero URL; then img fades in on load so we never flash fallback → uploaded */}
          {!heroSrc && <div className="men-hero__skeleton" aria-hidden="true" />}
          {heroSrc && (
            <picture>
              <ApiImage
                src={heroSrc}
                fallback="/hero.png"
                alt=""
                className={`men-hero__bg ${heroImageLoaded ? 'men-hero__bg--loaded' : ''}`}
                aria-hidden="true"
                width="1440"
                height="680"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                onLoad={() => setHeroImageLoaded(true)}
              />
            </picture>
          )}
          <div className="men-hero__overlay" aria-hidden="true" />
          <div className="men-hero__inner">
            <p className="men-hero__eyebrow" aria-hidden="true">
              <span className="men-hero__eyebrow-dot" />
              New Season · SS 2026
            </p>
            <h1 className="men-hero__title" id="men-hero-title">
              {currentMeta.h1}
            </h1>
            <p className="men-hero__subtitle">{currentMeta.subtitle}</p>
            {isMainPage && subcategories.length > 0 && (
              <div className="men-hero__ctas">
                {subcategories[0] && (
                  <Link
                    to={`${preferredBasePath}/${subcategories[0].slug}`}
                    className="men-hero__cta men-hero__cta--primary"
                  >
                    Shop {subcategories[0].name}
                    <IconArrowRight size={13} aria-hidden="true" />
                  </Link>
                )}
                {subcategories[1] && (
                  <Link
                    to={`${preferredBasePath}/${subcategories[1].slug}`}
                    className="men-hero__cta men-hero__cta--ghost"
                  >
                    Shop {subcategories[1].name}
                  </Link>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ── Trust Bar ──────────────────────────────────────────────────── */}
        <div className="men-trust-bar" role="list" aria-label="Shopping benefits">
          {[
            { icon: <IconTruck />,   label: 'Free Shipping' },
            { icon: <IconReturn />,  label: 'Returns for size & defective only' },
            { icon: <IconLock />,    label: 'Secure Checkout'                  },
            { icon: <IconShield />,  label: '100% Authentic'                   },
          ].map(({ icon, label }) => (
            <div key={label} className="men-trust-bar__item" role="listitem">
              <span className="men-trust-bar__icon" aria-hidden="true">{icon}</span>
              <span className="men-trust-bar__label">{label}</span>
            </div>
          ))}
        </div>

        {/* ── Category / Subcategory Errors ──────────────────────────────── */}
        {catError && (
          <div className="men-error" role="alert">
            <p className="men-error__message">{catError}</p>
            <button
              className="men-error__retry btn-primary"
              onClick={() => { setCatError(null); setCatFetchKey((k) => k + 1); }}
            >
              Retry
            </button>
          </div>
        )}

        {subError && !catError && (
          <div className="men-error" role="alert">
            <p className="men-error__message">{subError}</p>
            <button
              className="men-error__retry btn-primary"
              onClick={() => { setSubError(null); setCatFetchKey((k) => k + 1); }}
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Category Tiles — /men main page only ───────────────────────── */}
        {isMainPage && (
          <>
            {catLoading && (
              <div className="men-categories__loading" aria-label="Loading categories" aria-busy="true">
                <div className="men-categories__skeleton" />
              </div>
            )}

            {!catLoading && !catError && subcategories.length > 0 && (
              <section className="men-categories" aria-label="Shop by category">
                <p className="men-categories__label" aria-hidden="true">Shop by Category</p>
                <div className="men-categories__grid">
                  {subcategories.map((sub, index) => (
                    <Link
                      key={sub._id ?? sub.slug}
                      to={`${preferredBasePath}/${sub.slug}`}
                      className="men-category-tile"
                    >
                      <div className="men-category-tile__img-wrap">
                        <ApiImage
                          src={sub.image
                            ? getImageUrl(sub.image, sub.updatedAt, '/hero.png')
                            : (`/men-${sub.slug}.jpg` || '/hero.png')
                          }
                          fallback="/hero.png"
                          alt={sub.image?.alt || `${categorySlug === 'men' ? "Men's" : "Women's"} ${sub.name} — ${BRAND_NAME}`}
                          className="men-category-tile__img"
                          loading="lazy"
                          width="600"
                          height="800"
                          onLoad={(e) => e.currentTarget.classList.add('men-category-tile__img--loaded')}
                        />
                      </div>
                      <div className="men-category-tile__overlay" aria-hidden="true" />
                      <div className="men-category-tile__content">
                        <span className="men-category-tile__badge">
                          {index === 0 ? 'Bestseller' : 'New In'}
                        </span>
                        <h2 className="men-category-tile__title">{sub.name}</h2>
                        <span className="men-category-tile__cta">
                          Shop Now <IconArrowRight size={13} aria-hidden="true" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Launch-focused social bar — tailored for a new 2026 brand */}
            <div className="men-social-bar" aria-label="EVORA launch highlights">
              <div className="men-social-bar__inner">
                <div className="men-social-bar__stars">
                  <span className="men-social-bar__star-row" aria-hidden="true">
                    {[1, 2, 3, 4, 5].map((i) => <IconStar key={i} />)}
                  </span>
                  <span className="men-social-bar__rating">
                    Launching&nbsp;2026
                  </span>
                  <span className="men-social-bar__reviews">
                    · New independent label from India
                  </span>
                </div>
                <span className="men-social-bar__sep" aria-hidden="true" />
                <span className="men-social-bar__tag">
                  <strong>Free shipping</strong> across India · 7‑day size &amp; defect returns
                </span>
              </div>
            </div>
          </>
        )}

        {/* ── Filter + Sort Bar — subcategory pages only ─────────────────── */}
        {!isMainPage && (
          <div
            className="men-filters-bar"
            role="toolbar"
            aria-label="Filter and sort products"
          >
            <div className="men-filters-bar__left">
              <button
                className="men-filters-bar__filter-btn"
                onClick={() => setIsFilterOpen(true)}
                aria-expanded={isFilterOpen}
                aria-controls="filter-drawer"
                aria-label={`Filters${activeFilterCount > 0 ? `, ${activeFilterCount} active` : ''}`}
              >
                <IconFilter size={16} aria-hidden="true" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="men-filters-bar__badge" aria-hidden="true">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            <div className="men-filters-bar__right">
              {/* ✅ FIX #5 — count shown only after confirmed load */}
              {!prodLoading && totalProducts > 0 && (
                <span className="men-filters-bar__count" aria-live="polite">
                  {totalProducts.toLocaleString('en-IN')} products
                </span>
              )}
              <select
                className="men-filters-bar__sort"
                value={sort}
                onChange={handleSortChange}
                aria-label="Sort products by"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ── Products Grid ───────────────────────────────────────────────── */}
        {!isMainPage && (
          <section
            className="men-products"
            aria-label="Products"
            aria-busy={prodLoading}
          >
            <div className="men-products__container">

              {prodLoading ? (
                <div className="men-products__skeleton" aria-hidden="true">
                  {/* ✅ FIX #3 — prefixed skeleton keys */}
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={`skel-${i}`} className="men-products__skeleton-card shimmer" />
                  ))}
                </div>

              ) : prodError ? (
                <div className="men-products__error" role="alert">
                  <svg
                    width="40" height="40"
                    viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.2"
                    className="men-products__error-icon"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8"  x2="12"    y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <p className="men-products__error-title">We couldn't load these products</p>
                  <p className="men-products__error-sub">Check your connection and try again.</p>
                  {/* ✅ FIX #2 — retry via fetchKey, no orphaned controller */}
                  <button
                    className="men-products__error-retry btn-primary"
                    onClick={() => { setProdError(null); setProdFetchKey((k) => k + 1); }}
                  >
                    Try Again
                  </button>
                </div>

              ) : products.length > 0 ? (
                <>
                  {/* ✅ FIX #23 — product count as social proof */}
                  <p className="men-products__count" aria-live="polite">
                    <strong>{totalProducts.toLocaleString('en-IN')}</strong> products
                  </p>

                  <div className="men-products__grid" role="list">
                    {products.map((product, idx) => (
                      // ✅ FIX #4 — stable normalised pid
                      <div key={product.pid} role="listitem">
                        <ProductCard
                          product={product}
                          onAddToCart={(name) => handleAddToCart(name, product.pid)}
                          onProductClick={() => handleProductClick(product)}
                          priority={idx < 4}
                        />
                      </div>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <nav className="pagination" aria-label="Product pages">
                      {hasPrevPage ? (
                        <Link
                          className="pagination__btn pagination__btn--prev"
                          to={buildListUrl(page - 1)}
                          onClick={(e) => { e.preventDefault(); handlePageChange(page - 1); }}
                          aria-label="Previous page"
                          rel="prev"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6" />
                          </svg>
                          <span>Previous</span>
                        </Link>
                      ) : (
                        <span className="pagination__btn pagination__btn--prev" aria-disabled="true">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6" />
                          </svg>
                          <span>Previous</span>
                        </span>
                      )}
                      <div className="pagination__pages">
                        {buildPageNumbers(page, totalPages).map((item, i) =>
                          item === '...' ? (
                            <span key={`e-${i}`} className="pagination__ellipsis" aria-hidden="true">…</span>
                          ) : (
                            item === page ? (
                              <span
                                key={item}
                                className="pagination__page pagination__page--active"
                                aria-current="page"
                                aria-label={`Page ${item}`}
                              >
                                {item}
                              </span>
                            ) : (
                              <Link
                                key={item}
                                className="pagination__page"
                                to={buildListUrl(item)}
                                onClick={(e) => { e.preventDefault(); handlePageChange(item); }}
                                aria-label={`Page ${item}`}
                              >
                                {item}
                              </Link>
                            )
                          )
                        )}
                      </div>
                      {hasNextPage ? (
                        <Link
                          className="pagination__btn pagination__btn--next"
                          to={buildListUrl(page + 1)}
                          onClick={(e) => { e.preventDefault(); handlePageChange(page + 1); }}
                          aria-label="Next page"
                          rel="next"
                        >
                          <span>Next</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </Link>
                      ) : (
                        <span className="pagination__btn pagination__btn--next" aria-disabled="true">
                          <span>Next</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </span>
                      )}
                    </nav>
                  )}
                </>

              ) : (
                <div className="men-products__empty" role="status">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.2"
                    className="men-products__empty-icon" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                  <p className="men-products__empty-title">
                    {activeFilterCount > 0 ? "No matches for this combination" : "Nothing here yet"}
                  </p>
                  <p className="men-products__empty-sub">
                    {activeFilterCount > 0
                      ? 'Try clearing a filter or browse the full collection below.'
                      : 'New pieces are on the way — check back soon.'}
                  </p>
                  <div className="men-products__empty-actions">
                    {activeFilterCount > 0 && (
                      <button
                        className="men-products__empty-btn btn-ghost"
                        onClick={clearFilters}
                      >
                        Clear Filters
                      </button>
                    )}
                    {/* ✅ FIX #21 — always show parent category link */}
                    <Link to={preferredBasePath} className="men-products__empty-btn btn-ghost">
                      Browse All Men's
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── Recently Viewed ─────────────────────────────────────────────── */}
        {recentlyViewed.length > 0 && (
          <section className="recently-viewed" aria-label="Recently viewed">
            <div className="recently-viewed__container">
              <div className="recently-viewed__header">
                <h2 className="recently-viewed__title">Recently Viewed</h2>
                <div className="recently-viewed__header-actions">
                  {/* ✅ FIX #22 — "View all" escape from the 5-item truncation */}
                  <Link to="/recently-viewed" className="recently-viewed__view-all">
                    View all
                  </Link>
                  <button
                    className="recently-viewed__clear"
                    onClick={() => {
                      localStorage.removeItem('recentlyViewed');
                      setRecentlyViewed([]);
                    }}
                    aria-label="Clear recently viewed products"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="recently-viewed__scroll">
                {recentlyViewed.slice(0, 5).map((product, index) => (
                  <Link
                    key={`recent-${product._id ?? product.id}-${index}`}
                    to={`/product/${product.slug ?? product._id}`}
                    className="recently-viewed__item"
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="recently-viewed__image-wrap">
                      <ApiImage
                        src={product.images?.[0]?.url ?? product.images?.[0]}
                        fallback="/hero.png"
                        alt={product.name}
                        className="recently-viewed__image"
                        loading="lazy"
                        width="160"
                        height="213"
                      />
                    </div>
                    <p className="recently-viewed__name">{product.name}</p>
                    <p className="recently-viewed__price">
                      {formatCurrency(product.price ?? 0)}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── SEO Content ─────────────────────────────────────────────────── */}
        <section className="men-seo" aria-label="About Shop Men">
          <div className="men-seo__grid">
            <div>
              <h2 className="men-seo__title">Premium Men's T-Shirts Online in India</h2>
              <p className="men-seo__text">
                Discover {BRAND_NAME}'s men's premium t-shirts, designed
                for the modern gentleman. From comfortable round neck t-shirts to classic polo
                shirts, each piece is crafted with attention to detail and timeless elegance.
              </p>
              <p className="men-seo__text">
                Explore our range of{' '}
                <Link to={`${preferredBasePath}/round-neck`}>men's round neck t-shirts</Link> and{' '}
                <Link to={`${preferredBasePath}/polo`}>premium polo t-shirts</Link>.{' '}
                Free shipping on all orders. Returns for size &amp; defective only ({RETURNS_DAYS} days).
              </p>
            </div>
            <div>
              <h3 className="men-seo__title men-seo__title--sm">Quick Links</h3>
              <p className="men-seo__text"><Link to={`${preferredBasePath}/round-neck`}>Men's Round Neck T-Shirts</Link></p>
              <p className="men-seo__text"><Link to={`${preferredBasePath}/polo`}>Men's Polo T-Shirts</Link></p>
              <p className="men-seo__text"><Link to="/size-guide">Size Guide</Link></p>
              <p className="men-seo__text"><Link to="/shipping">Shipping &amp; Returns</Link></p>
            </div>
          </div>
        </section>

      </div>

      {/* ── Filter Drawer ───────────────────────────────────────────────── */}
      {!isMainPage && (
        <FilterDrawer
          id="filter-drawer"
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          subCategories={subcategories.map((s) => s.slug)}
        />
      )}
    </Layout>
  );
}

export default Men;
